import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, Pagination, StatusPill, fmtDate, EmptyState } from "./_shared";

const LIMIT = 50;
const STATUS = ["", "active", "inactive", "cancelling", "past_due"];

export default function AdminSubscriptions() {
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/subscriptions", {
          params: { status: status || undefined, skip, limit: LIMIT },
        });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, skip]);

  return (
    <AdminLayout
      title="Subscriptions"
      subtitle="$50/month memberships, including their 3-month commitment progress."
    >
      <div className="space-y-5">
        <Card className="p-4" testid="admin-subs-filters">
          <select
            value={status}
            onChange={(e) => {
              setSkip(0);
              setStatus(e.target.value);
            }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
            data-testid="admin-subs-status-select"
          >
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {s ? `Status: ${s}` : "All statuses"}
              </option>
            ))}
          </select>
        </Card>

        <Card className="overflow-hidden" testid="admin-subs-table-card">
          {loading ? (
            <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500" data-testid="admin-subs-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState
              title="No subscriptions"
              body="When users subscribe they'll show up here."
              testid="admin-subs-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">User</th>
                    <th className="text-left px-5 py-2.5 font-medium">Status</th>
                    <th className="text-left px-5 py-2.5 font-medium">Months paid</th>
                    <th className="text-left px-5 py-2.5 font-medium">Started</th>
                    <th className="text-left px-5 py-2.5 font-medium">Period end</th>
                    <th className="text-left px-5 py-2.5 font-medium">Cancel</th>
                    <th className="px-5 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="admin-subs-tbody">
                  {data.items.map((s) => {
                    const monthsPaid = s.months_paid ?? 0;
                    const min = s.min_months ?? 3;
                    return (
                      <tr
                        key={s.user_id}
                        className="hover:bg-slate-50"
                        data-testid={`admin-subs-row-${s.user_id}`}
                      >
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-900">
                            {s.user?.name || s.user?.email || s.user_id}
                          </div>
                          <div className="text-xs text-slate-500">{s.user?.email}</div>
                        </td>
                        <td className="px-5 py-3">
                          <StatusPill
                            value={s.status}
                            tone={
                              s.status === "active"
                                ? "green"
                                : s.status === "cancelling"
                                  ? "amber"
                                  : s.status === "past_due"
                                    ? "rose"
                                    : "slate"
                            }
                          />
                        </td>
                        <td className="px-5 py-3 text-slate-700">
                          {monthsPaid} <span className="text-slate-400">/ min {min}</span>
                        </td>
                        <td className="px-5 py-3 text-slate-500">
                          {s.started_at ? fmtDate(s.started_at) : "—"}
                        </td>
                        <td className="px-5 py-3 text-slate-500">
                          {s.current_period_end ? fmtDate(s.current_period_end) : "—"}
                        </td>
                        <td className="px-5 py-3 text-slate-500">
                          {s.cancel_at_period_end ? "Yes" : "—"}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <Link
                            to={`/admin/users/${s.user_id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
                            data-testid={`admin-subs-view-${s.user_id}`}
                          >
                            View user <ChevronRight className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && data.items.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                total={data.total}
                skip={skip}
                limit={LIMIT}
                onChange={setSkip}
                testidPrefix="admin-subs"
              />
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
